import React, { useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import Radio from "../../library/Radio";
import TextInput from "../../library/TextInput";
import ResizableTextarea from "../../library/ResizableTextarea";
import PositiveButton from "../../library/PositiveButton";
import Button from "../../library/Button";
import CommonButton from "../../common/Button/Button";
import { SubmitOhsBuildingCheck } from "../../../services/FirstAidBoxService";
import { invalidateFirstAidBoxQueries, useOhsBuildingCheckForm } from "../../../query/hooks/QueryHooksFirstAidBox";
import { BUILDING_CHECK_ANSWER_LABELS, BUILDING_CHECK_ANSWERS, OHS_BASE_PATH } from "./ohsConstants";
import { getOhsErrorMessage, isIssueAnswer, numberChecklistQuestions } from "./ohsUtils";
import {
    AnswerOptions,
    QuestionRow,
    QuestionText,
    CategoryHeading,
    ConditionBadge,
    ConfirmationPanel,
    MutedText,
    QuestionNumber,
    SectionCard,
    SectionHeader,
    SectionTitle,
    WrappingButtonGroup
} from "./Ohs.styles";

const EMPTY_ANSWER = { answer: "", comment: "" };

/**
 * Monthly building check - the OHS Representative walks the building and answers every checklist question
 * Yes, No or N/A. A No needs a comment saying what needs attention.
 * @param {Object} props
 * @param {string} props.buildingId Building being checked.
 */
function BuildingCheckForm({ buildingId }) {
    const history = useHistory();
    const { data: buildingCheckForm, isLoading } = useOhsBuildingCheckForm(buildingId);
    const [answers, setAnswers] = useState({});
    const [notes, setNotes] = useState("");
    const [answerErrors, setAnswerErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submittedCheck, setSubmittedCheck] = useState(null);

    const numberedQuestions = useMemo(() => numberChecklistQuestions(buildingCheckForm?.questions || []), [buildingCheckForm]);

    /**
     * Updates one question's answer or comment, clearing its error.
     * @param {number} questionId ohs_building_check_questions.id
     * @param {string} fieldName answer | comment
     * @param {string} value New value.
     */
    const handleAnswerChange = (questionId, fieldName, value) => {
        setAnswers((currentAnswers) => ({
            ...currentAnswers,
            [questionId]: { ...EMPTY_ANSWER, ...currentAnswers[questionId], [fieldName]: value },
        }));
        setAnswerErrors((currentErrors) => ({ ...currentErrors, [questionId]: undefined }));
    };

    const handleSubmit = async () => {
        const submitErrors = {};
        numberedQuestions.forEach((checklistQuestion) => {
            const questionAnswer = answers[checklistQuestion.id] || EMPTY_ANSWER;
            if (!questionAnswer.answer) {
                submitErrors[checklistQuestion.id] = "Choose Yes, No or N/A";
            } else if (isIssueAnswer(questionAnswer.answer, checklistQuestion.expected_answer) && !questionAnswer.comment.trim()) {
                submitErrors[checklistQuestion.id] = "Say what needs attention";
            }
        });
        if (Object.keys(submitErrors).length) {
            setAnswerErrors(submitErrors);
            Swal.fire("Not finished yet", `${Object.keys(submitErrors).length} of the questions still need an answer or a comment - they're marked in red.`, "warning");
            return;
        }

        const countAnswers = (answerValue) => numberedQuestions.filter((checklistQuestion) => answers[checklistQuestion.id].answer === answerValue).length;
        const issueCount = numberedQuestions.filter((checklistQuestion) => isIssueAnswer(answers[checklistQuestion.id].answer, checklistQuestion.expected_answer)).length;
        try {
            const confirmation = await Swal.fire({
                title: "Submit this building check?",
                text: `${buildingCheckForm.building.building_name} (${buildingCheckForm.building.campus_name}): ${countAnswers(BUILDING_CHECK_ANSWERS.yes)} Yes, ${countAnswers(BUILDING_CHECK_ANSWERS.no)} No, ${countAnswers(BUILDING_CHECK_ANSWERS.notApplicable)} N/A - ${issueCount ? `${issueCount} ${issueCount === 1 ? "issue" : "issues"}` : "all clear"}.`,
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "var(--button-color)",
                cancelButtonColor: "var(--states-alert)",
                confirmButtonText: "Yes, submit",
            });
            if (!confirmation.isConfirmed) {
                return;
            }

            setIsSubmitting(true);
            const { data } = await SubmitOhsBuildingCheck({
                buildingId: buildingCheckForm.building.building_id,
                answers: numberedQuestions.map((checklistQuestion) => ({
                    questionId: checklistQuestion.id,
                    answer: answers[checklistQuestion.id].answer,
                    comment: answers[checklistQuestion.id].comment.trim() || undefined,
                })),
                notes: notes.trim() || undefined,
            });
            await invalidateFirstAidBoxQueries();
            setSubmittedCheck(data.data);
        } catch (error) {
            console.log("BuildingCheckForm.js->handleSubmit->Error", error);
            Swal.fire("Error", getOhsErrorMessage(error, "The building check could not be submitted."), "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <SkeletonTicketList />;
    }
    if (!buildingCheckForm) {
        return <MutedText>This building could not be found.</MutedText>;
    }

    if (submittedCheck) {
        return (
            <ConfirmationPanel>
                <i className="fal fa-check-circle"></i>
                <SectionTitle>Building check submitted</SectionTitle>
                <MutedText>
                    Building check of {buildingCheckForm.building.building_name} ({buildingCheckForm.building.campus_name}) recorded on {moment(submittedCheck.completedAt).format("D MMMM YYYY")}.
                </MutedText>
                <ConditionBadge $condition={submittedCheck.issueCount > 0 ? "damaged" : "ok"}>
                    {submittedCheck.issueCount > 0
                        ? `${submittedCheck.issueCount} ${submittedCheck.issueCount === 1 ? "issue needs" : "issues need"} attention`
                        : "All clear"}
                </ConditionBadge>
                <WrappingButtonGroup>
                    <Button as={Link} to={`${OHS_BASE_PATH}/buildingChecks`}>Back to Building Checks</Button>
                    <Button as={Link} to={`${OHS_BASE_PATH}/buildingCheckHistory`} variant="secondary">View Building Check History</Button>
                </WrappingButtonGroup>
            </ConfirmationPanel>
        );
    }

    return (
        <SectionCard>
            <SectionHeader>
                <div>
                    <SectionTitle>Building Check: {buildingCheckForm.building.building_name}</SectionTitle>
                    <MutedText>
                        {buildingCheckForm.building.campus_name}{" · "}
                        {buildingCheckForm.building.ohs_representatives.length
                            ? `OHS Representatives: ${buildingCheckForm.building.ohs_representatives.map((ohsRepresentative) => ohsRepresentative.name).join(", ")}`
                            : "No OHS Representative assigned"}
                        {" · "}Answer every question. An answer in red flags an issue and needs a comment saying what needs attention.
                    </MutedText>
                </div>
            </SectionHeader>

            {numberedQuestions.map((checklistQuestion, questionIndex) => {
                const questionAnswer = answers[checklistQuestion.id] || EMPTY_ANSWER;
                const answerError = answerErrors[checklistQuestion.id];
                const isNewSection = numberedQuestions[questionIndex - 1]?.section !== checklistQuestion.section;

                return (
                    <React.Fragment key={checklistQuestion.id}>
                        {isNewSection && <CategoryHeading>{checklistQuestion.section}</CategoryHeading>}
                        <QuestionRow>
                            <QuestionText>
                                <QuestionNumber>{checklistQuestion.number}.</QuestionNumber>
                                <span>
                                    {checklistQuestion.question}
                                    {checklistQuestion.expected_answer === BUILDING_CHECK_ANSWERS.no && (
                                        <div><MutedText>No is the expected answer</MutedText></div>
                                    )}
                                </span>
                            </QuestionText>
                            <AnswerOptions role="radiogroup" aria-label={`Question ${checklistQuestion.number} answer`}>
                                {Object.values(BUILDING_CHECK_ANSWERS).map((answerValue) => (
                                    <Radio
                                        key={answerValue}
                                        size="md"
                                        variant={isIssueAnswer(answerValue, checklistQuestion.expected_answer) ? "destructive" : "primary"}
                                        name={`ohsBuildingAnswer${checklistQuestion.id}`}
                                        value={answerValue}
                                        label={BUILDING_CHECK_ANSWER_LABELS[answerValue]}
                                        checked={questionAnswer.answer === answerValue}
                                        error={Boolean(answerError) && !questionAnswer.answer}
                                        onChange={() => handleAnswerChange(checklistQuestion.id, "answer", answerValue)}
                                    />
                                ))}
                            </AnswerOptions>
                            <TextInput
                                aria-label={`Question ${checklistQuestion.number} comment`}
                                size="md"
                                maxLength={1000}
                                placeholder={questionAnswer.answer && isIssueAnswer(questionAnswer.answer, checklistQuestion.expected_answer) ? "What needs attention?" : "Comment (optional)"}
                                value={questionAnswer.comment}
                                onChange={(event) => handleAnswerChange(checklistQuestion.id, "comment", event.target.value)}
                                error={Boolean(answerError)}
                                errorText={answerError}
                            />
                        </QuestionRow>
                    </React.Fragment>
                );
            })}

            <CategoryHeading>General Comments</CategoryHeading>
            <ResizableTextarea
                id="ohsBuildingCheckNotes"
                placeholder="Anything else worth noting about the building (optional)"
                rows={3}
                maxLength={5000}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
            />

            <WrappingButtonGroup className="mt-4">
                <PositiveButton onClick={handleSubmit} disabled={isSubmitting}>
                    Submit Building Check
                </PositiveButton>
                <CommonButton type="button" variant="danger" onClick={() => history.push(`${OHS_BASE_PATH}/buildingChecks`)} iconLeft={<i className="fal fa-times"></i>}>
                    Cancel
                </CommonButton>
            </WrappingButtonGroup>
        </SectionCard>
    );
}

export default BuildingCheckForm;
